import { MAX_RELAUNCH_QUERY_TIMES } from '@core/constants/API';
import { SEARCHER_TYPES, SEARCHER_OPTIONS } from '@core/constants/SEARCHER';
import { DEFAULT_DARK_MODE_VALUE } from '@core/constants/DEFAULT';
import { getWesogUserInfo } from '@core/utils/wesog-user';
import { getTokensFromRefreshToken, removeRefreshTokenAndLogoutUser } from './oauth';
import { getStoreConfiguration, getStoreConfigurationLink } from './store';
import {
    WESOG_SESSION_STORE_PARAMS,
    WESOG_STORE_CONFIGURATION_PARAMS,
} from '@core/constants/STORE';
import { ROUTES } from '@core/constants/ROUTES';
import { getSessionStoreValue, updateSessionStoreValue } from './sessionStore';
import { goToHomeView } from '@core/utils/navigate';
import {
    CUSTOM_MAIN_LOADING_SPINNER_WEB_COMPONENT,
    MAIN_BASE_LAYOUT_WEB_COMPONENT,
} from '@core/constants/WEB-COMPONENT-KEYS';
import { TRIGGER_SHOW_LOADING_COMPONENT_EVENT } from '@core/constants/EVENTS';

/**
 * Check if the browser supports ShadowDOM and Web Components
 * @returns {boolean} True if ShadowDOM is supported, false otherwise
 */
const supportsWebComponents = () => {
    return (
        'customElements' in window &&
        'attachShadow' in Element.prototype &&
        'getRootNode' in Element.prototype &&
        'content' in document.createElement('template')
    );
};

/**
 * Show alert for unsupported browsers and provide fallback instructions
 * @returns {void}
 */
const showUnsupportedBrowserAlert = () => {
    const message = `Tu navegador no es compatible con las características avanzadas de esta aplicación.

Para una mejor experiencia, te recomendamos:
• Actualizar tu navegador a la última versión
• Usar Chrome, Firefox, Safari o Edge actualizados
• Habilitar JavaScript si está deshabilitado

La aplicación funcionará con funcionalidad limitada.`;

    alert(message);

    // Optional: Add a visual indicator in the page
    const fallbackDiv = document.createElement('div');
    fallbackDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        background: #ff6b6b;
        color: white;
        padding: 10px;
        text-align: center;
        z-index: 10000;
        font-family: Arial, sans-serif;
        font-size: 14px;
    `;
    fallbackDiv.textContent = '⚠️ Navegador no compatible - Funcionalidad limitada';
    document.body.appendChild(fallbackDiv);
};

/**
 * Clean the URL params by removing empty values.
 * @type {function}
 * @param {URLSearchParams} urlParams - The URL.
 * @returns {URLSearchParams} - The cleaned URL params.
 */
export const cleanUrlParams = (urlParams) => {
    urlParams.forEach((value, key) => {
        if (value === '' || (Array.isArray(value) && value.length === 0)) {
            urlParams.delete(key);
        }
    });

    return urlParams;
};

/**
 * Relaunch the query if the status is 401 or 403 to get the new access token.
 * @type {function}
 * @param {Response} response - The response from the server.
 * @param {object} options - The params to send.
 * @param {number} options.times - The number of times that the query has been relaunched.
 * @param {function} fn - The function to relaunch the query.
 * @returns {Promise<boolean>} - Returns true if the query was relaunched, false otherwise.
 */
export const relaunchQuery401or403status = async (response, options, fn) => {
    // Handle 401 or 403 status codes
    if (response.status === 401 || response.status === 403) {
        if (options.times >= MAX_RELAUNCH_QUERY_TIMES) {
            await removeRefreshTokenAndLogoutUser();
            return false;
        }

        /** @type {{ username: string, access_token: string, refresh_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string, lightbox: string}} */
        const user = getWesogUserInfo();

        try {
            await getTokensFromRefreshToken(user.refresh_token);

            // Relaunch the function with incremented times counter
            const result = await fn({ times: options.times + 1, ...options });
            return result;
        } catch (error) {
            if (error?.message?.includes('Invalid refresh token')) {
                await removeRefreshTokenAndLogoutUser();
                return false;
            }
            // Re-throw other errors to be handled by the caller
            throw error;
        }
    }

    // Handle specific 400 error for invalid refresh token
    if (response.status === 400 && response.statusText === 'Invalid refresh token') {
        await removeRefreshTokenAndLogoutUser();
        return false;
    }

    // Handle 500 server errors
    if (response.status === 500) {
        throw new Error(`${response.status} - ${response.statusText}`);
    }

    // Return false for other status codes (not relaunched)
    return false;
};

/**
 * Handle 401 or 403 status codes without relaunching the query.
 * This function is used when you want to handle authentication errors
 * but don't want to retry the original request.
 * @type {function}
 * @param {object} params - The parameters object.
 * @param {Response} params.response - The response from the server.
 * @returns {Promise<void>}
 */
export const handleQuery401or403status = async ({ response }) => {
    if (response.status === 401 || response.status === 403) {
        await removeRefreshTokenAndLogoutUser();
        return;
    }

    if (response.status === 400 && response.statusText === 'Invalid refresh token') {
        await removeRefreshTokenAndLogoutUser();
        return;
    }

    if (response.status === 500) {
        throw new Error(`${response.status} - ${response.statusText}`);
    }
};

/**
 * Check if the user is in creative mode.
 * @returns {boolean} - The user is in creative mode.
 */
export const isCreativeMode = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    return (
        urlParams.has(SEARCHER_TYPES.OPTION) &&
        urlParams.get(SEARCHER_TYPES.OPTION).includes(SEARCHER_OPTIONS.CR_CODE)
    );
};

/**
 * Check if the user is in editorial video mode.
 * @returns {boolean} - The user is in editorial video
 */
export const isEditorialVideoMode = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    return (
        urlParams.has(SEARCHER_TYPES.OPTION) &&
        urlParams.get(SEARCHER_TYPES.OPTION).includes(SEARCHER_OPTIONS.ED_VIDEOS)
    );
};

/**
 * Show the spinner button.
 * @param {HTMLButtonElement} button - The button.
 * @param {boolean} [positionRelative=true] - The position relative. Default is true.
 * @returns {void}
 */
export const showSpinnerButton = (button, positionRelative = true) => {
    const /** @type {HTMLElement} */ spinner = document.createElement('span');
    spinner.classList.add('spinner');

    button.innerHTML = '';
    if (positionRelative) button.style.position = 'relative';
    button.appendChild(spinner);
};

/**
 * Open a link depending on the configuration. If the configuration is true, the link is opened in the same tab.
 * @param {string} url - The URL.
 * @returns {void}
 */
export const openLink = (url) => {
    if (getStoreConfigurationLink()) {
        window.open(url, '_blank');
    } else {
        window.location.href = url;
    }
};

/**
 * Check if the Dark Mode is enabled.
 * @returns {boolean} - The Dark Mode is enabled.
 */
export const isDarkModeEnabled = () => {
    const /** @type {Object} */ configuration = getStoreConfiguration();
    return configuration
        ? configuration[WESOG_STORE_CONFIGURATION_PARAMS.DARK_MODE]
        : DEFAULT_DARK_MODE_VALUE;
};

/**
 * Check the dark mode configuration.
 * @returns {void}
 */
export const checkDarkMode = () => {
    document.documentElement.setAttribute(
        'data-theme',
        isDarkModeEnabled() ? 'dark' : 'light',
    );

    const /** @type {HTMLElement | null} */ mainSpinner = document.querySelector(
            CUSTOM_MAIN_LOADING_SPINNER_WEB_COMPONENT,
        );

    if (mainSpinner) {
        mainSpinner.style.display = 'none';
    }

    const /** @type {HTMLElement | null} */ mainBaseLayout = document.querySelector(
            MAIN_BASE_LAYOUT_WEB_COMPONENT,
        );

    if (mainBaseLayout) {
        mainBaseLayout.style.opacity = '1';
    }
};

/**
 * Create a custom debounce function to avoid multiple calls.
 * @param {Function} func - The function to call.
 * @param {number} wait - The delay in milliseconds.
 * @returns {Function} - The custom debounce function.
 */
export const customDebounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

/**
 * Check if we are in the Home view.
 * @returns {boolean} True if we are in the Home view, otherwise false.
 */
export const isNotHomeView = () => {
    return (
        [
            ROUTES.HOME,
            ROUTES.HOME_CREATIVE,
            ROUTES.HOME_EDITORIAL,
            ROUTES.HOME_EN,
            ROUTES.HOME_CREATIVE_EN,
            ROUTES.HOME_EDITORIAL_EN,
        ].includes(window.location.pathname) || window.location.pathname === '/'
    );
};

/**
 * Update the meta tags.
 * @param {Object} params - The params.
 * @param {string} params.title - The title.
 * @param {string} params.description - The description.
 * @param {string} params.image - The image. Default is undefined.
 * @returns {void}
 */
export const updateMetaTags = ({ title, description, image = undefined }) => {
    // Titles
    const /** @type {HTMLMetaElement} */ metaTitle =
            document.querySelector('meta[name="title"]');

    // Descriptions
    const /** @type {HTMLMetaElement} */ metaDescription = document.querySelector(
            'meta[name="description"]',
        );
    const /** @type {HTMLMetaElement} */ ogDescription = document.querySelector(
            'meta[property="og:description"]',
        );

    if (metaTitle) {
        metaTitle.setAttribute('content', title);
    }

    document.title = title;

    if (metaDescription) {
        metaDescription.setAttribute('content', description);
    }

    if (ogDescription) {
        ogDescription.setAttribute('content', description);
    }

    // OG Image
    // - the details view
    // - the report details view
    if (image) {
        const /** @type {HTMLMetaElement} */ ogImage = document.querySelector(
                'meta[property="og:image"]',
            );

        if (ogImage) {
            ogImage.setAttribute('content', image);
        }
    }
};

/**
 * Update the default flag to redirect to the home report view.
 * @param {boolean} goToHome - The function to go to the home view.
 * @returns {void}
 */
export const checkDefaultView = (goToHome = false) => {
    const /** @type {string | null} */ redirectFlag =
            getSessionStoreValue(WESOG_SESSION_STORE_PARAMS.REDIRECT_HOME_REPORT_VIEW) ===
            'true';

    if (!redirectFlag) {
        updateSessionStoreValue(
            WESOG_SESSION_STORE_PARAMS.REDIRECT_HOME_REPORT_VIEW,
            'true',
        );

        if (goToHome) goToHomeView();
    }
};

/**
 * Check if the view is the details view.
 * @returns {boolean} - The view is the details view.
 */
export const isDetailsView = () => {
    return window.location.pathname.includes(ROUTES.DETAILS);
};

/**
 * Check if the current environment is local host.
 * @returns {boolean} - True if the current environment is local host.
 */
export const isLocalHost = () => {
    return (
        window.location.href.includes('localhost') ||
        window.location.href.includes('127.0.0.1')
    );
};

/**
 * Check if the current environment is development mode.
 * @returns {boolean} - True if the current environment is development mode.
 */
export const isDevMode = () => {
    const PORT_DEV = [':444', ':445'];

    return PORT_DEV.some((port) => window.location.href.includes(port));
};

/**
 * Check if the current pathname is Details or Report Details view.
 * @returns {boolean} True if the current pathname is Details or Report Details views, otherwise false.
 */
export const fixedSearcherOnHeader = () => {
    const currentPathname = window.location.pathname;

    return (
        currentPathname === ROUTES.DETAILS || currentPathname === ROUTES.REPORT_DETAILS
    );
};

/**
 * Clean the text to cards format.
 * @param {string} text - Text to clean.
 * @returns {string} - The text cleaned.
 */
export const cleanTextToCards = (text) => {
    return text.replace(/(<([^>]+)>)/gi, '').replace(/\n/g, '');
};

/**
 * Update the window history
 * @param {URLSearchParams} params - The URLSearchParams object
 * @returns {void}
 */
export const updateWindowHistory = (params) => {
    const /** @type {string} */ newUrl = `${
            window.location.pathname
        }?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
};

/**
 * Replace the window history
 * @param {URLSearchParams} params - The URLSearchParams object
 * @returns {void}
 */
export const replaceWindowHistory = (params) => {
    const /** @type {string} */ newUrl = `${
            window.location.pathname
        }?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
};

/**
 * Get the key by value from an object
 * @param {object} object - The object to search.
 * @param {string} value - The value to search.
 * @returns {string}
 */
export const getKeyByValue = (object, value) => {
    return Object.keys(object).find((key) => object[key] === value);
};

/**
 * Capitalize the first letter of a string
 * @param {string} string - The string to capitalize.
 * @returns {string}
 */
export const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Lower case the first letter of a string
 * @param {string} string - The string to lower case.
 * @returns {string}
 */
export const lowerCaseLessFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

/**
 * Get the main body base layout web component.
 * @returns {HTMLElement | null}
 */
export const getMainBodyBaseLayout = () => {
    return (
        document.querySelector(MAIN_BASE_LAYOUT_WEB_COMPONENT).querySelector('.body') ||
        null
    );
};

/**
 * Check if the img is a similar image.
 * @returns {boolean} True if the img is a similar image, otherwise false.
 */
export const isSimilarImg = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    return (
        urlParams.has(SEARCHER_TYPES.PROVIDER) && urlParams.has(SEARCHER_TYPES.IMG_UID)
    );
};

/**
 * Show the loading spinner.
 * @returns {void}
 */
export const showLoadingSpinner = () => {
    document.dispatchEvent(
        new CustomEvent(TRIGGER_SHOW_LOADING_COMPONENT_EVENT, {
            detail: {
                unauthorized: true,
            },
        }),
    );
};

/**
 * Check if the plugin AI is activated.
 * @returns {boolean} True if the plugin AI is activated, otherwise false.
 */
export const isPluginAIActivated = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    return urlParams.has(SEARCHER_TYPES.PLUGIN_AI);
};

/**
 * Check if the user is in creative mode based on URL path
 * @returns {boolean} - The user is in creative mode.
 */
export const isCreativeModeFromPath = () => {
    return window.location.pathname === ROUTES.HOME_CREATIVE;
};

/**
 * Check if the user is in editorial mode based on URL path
 * @returns {boolean} - The user is in editorial mode.
 */
export const isEditorialModeFromPath = () => {
    return window.location.pathname === ROUTES.HOME_EDITORIAL;
};

/**
 * Check ShadowDOM support and handle unsupported browsers
 * @returns {void}
 */
export const checkShadowDOMSupport = () => {
    if (!supportsWebComponents()) {
        console.warn('ShadowDOM/Web Components not supported in this browser');
        showUnsupportedBrowserAlert();

        // Add a class to body for CSS-based fallbacks
        document.body.classList.add('no-shadowdom-support');

        // Hide web components and show fallback content
        const webComponents = document.querySelectorAll(
            'main-header, main-searcher-component, main-footer',
        );
        webComponents.forEach((component) => {
            component.style.display = 'none';
        });

        // Show the noscript fallback content
        const noscriptFallback = document.querySelector('.seo-fallback');
        if (noscriptFallback) {
            noscriptFallback.style.display = 'block';
        }
    }
};
